import { onBeforeUnmount, onMounted, watch, type InjectionKey, type Ref } from 'vue'

import { normalizePointer, type PointerPosition } from '../lib/experience'
import {
  dampPointerValue,
  getEffectivePointerKind,
  getLocalPointer,
  getPointerDepthStrength,
  shouldReturnPointerToRest,
  type PointerKind,
} from '../lib/pointerMotion'

export interface PointerMotionFrame extends PointerPosition {
  clientX: number
  clientY: number
  velocity: number
  active: boolean
  kind: PointerKind
}

export interface PointerMotionController {
  getSnapshot: () => PointerMotionFrame
  subscribe: (listener: (frame: PointerMotionFrame) => void) => () => void
}

export const pointerMotionKey: InjectionKey<PointerMotionController> = Symbol(
  'se-universe-pointer-motion',
)

const zeroFrame = (): PointerMotionFrame => ({
  x: 0,
  y: 0,
  clientX: 0,
  clientY: 0,
  velocity: 0,
  active: false,
  kind: 'mouse',
})

export function usePointerMotion(
  root: Ref<HTMLElement | null>,
  disabled: Ref<boolean>,
): PointerMotionController {
  const subscribers = new Set<(frame: PointerMotionFrame) => void>()
  const frame = zeroFrame()

  let targetX = 0
  let targetY = 0
  let targetClientX = 0
  let targetClientY = 0
  let targetVelocity = 0
  let localX = 0
  let localY = 0
  let targetLocalX = 0
  let targetLocalY = 0
  let localPercentX = 50
  let localPercentY = 50
  let targetPercentX = 50
  let targetPercentY = 50
  let lastInputAt = 0
  let lastPointerAt = 0
  let previousClientX = 0
  let previousClientY = 0
  let lastFrameAt = 0
  let rafId = 0
  let running = false
  let depthTarget: HTMLElement | null = null
  let interactive = false
  let listenersAttached = false
  let hasFinePointer = false

  const writeRootVariables = () => {
    const element = root.value
    if (!element) return
    element.style.setProperty('--pointer-x-eased', frame.x.toFixed(4))
    element.style.setProperty('--pointer-y-eased', frame.y.toFixed(4))
    element.style.setProperty('--pointer-client-x', `${frame.clientX.toFixed(2)}px`)
    element.style.setProperty('--pointer-client-y', `${frame.clientY.toFixed(2)}px`)
    element.style.setProperty('--pointer-velocity', frame.velocity.toFixed(3))
    element.style.setProperty('--pointer-far-x', `${(-frame.x * 8).toFixed(2)}px`)
    element.style.setProperty('--pointer-far-y', `${(frame.y * 6).toFixed(2)}px`)
    element.style.setProperty('--pointer-mid-x', `${(frame.x * 14).toFixed(2)}px`)
    element.style.setProperty('--pointer-mid-y', `${(-frame.y * 10).toFixed(2)}px`)
    element.style.setProperty('--pointer-core-x', `${(frame.x * 20).toFixed(2)}px`)
    element.style.setProperty('--pointer-core-y', `${(-frame.y * 14).toFixed(2)}px`)
    element.style.setProperty('--pointer-front-x', `${(frame.x * 34).toFixed(2)}px`)
    element.style.setProperty('--pointer-front-y', `${(-frame.y * 24).toFixed(2)}px`)
    element.style.setProperty('--pointer-heading-x', `${(frame.x * 3.5).toFixed(2)}px`)
    element.style.setProperty('--pointer-heading-y', `${(-frame.y * 2.5).toFixed(2)}px`)
    element.dataset.pointerInteractive = interactive ? 'true' : 'false'
  }

  const clearDepthTarget = () => {
    if (!depthTarget) return
    depthTarget.style.removeProperty('--local-x')
    depthTarget.style.removeProperty('--local-y')
    depthTarget.style.removeProperty('--local-percent-x')
    depthTarget.style.removeProperty('--local-percent-y')
    depthTarget.style.removeProperty('--tilt-x')
    depthTarget.style.removeProperty('--tilt-y')
    depthTarget.style.removeProperty('--magnet-x')
    depthTarget.style.removeProperty('--magnet-y')
    depthTarget.style.removeProperty('--layer-near-x')
    depthTarget.style.removeProperty('--layer-near-y')
    depthTarget.style.removeProperty('--layer-far-x')
    depthTarget.style.removeProperty('--layer-far-y')
    depthTarget.style.removeProperty('--project-tilt-x')
    depthTarget.style.removeProperty('--project-tilt-y')
    depthTarget.style.removeProperty('--project-image-x')
    depthTarget.style.removeProperty('--project-image-y')
    depthTarget.style.removeProperty('--project-copy-x')
    depthTarget.style.removeProperty('--project-copy-y')
    depthTarget.style.removeProperty('--project-foreground-x')
    depthTarget.style.removeProperty('--project-foreground-y')
    depthTarget = null
    targetLocalX = 0
    targetLocalY = 0
    targetPercentX = 50
    targetPercentY = 50
  }

  const setDepthTarget = (nextTarget: HTMLElement | null, event: PointerEvent) => {
    if (nextTarget !== depthTarget) {
      clearDepthTarget()
      depthTarget = nextTarget
    }
    if (!depthTarget) return

    const local = getLocalPointer(event.clientX, event.clientY, depthTarget.getBoundingClientRect())
    targetLocalX = local.x
    targetLocalY = local.y
    targetPercentX = local.percentX
    targetPercentY = local.percentY
  }

  const requestFrame = () => {
    if (rafId || disabled.value || document.hidden) return
    lastFrameAt = performance.now()
    rafId = requestAnimationFrame(renderFrame)
  }

  const handlePointer = (event: PointerEvent) => {
    if (disabled.value) return
    if (root.value) root.value.dataset.pointerPresent = 'true'
    const now = performance.now()
    const reportedKind: PointerKind =
      event.pointerType === 'touch' ? 'touch' : event.pointerType === 'pen' ? 'pen' : 'mouse'
    const kind = getEffectivePointerKind(reportedKind, hasFinePointer)
    const strength = getPointerDepthStrength(kind, disabled.value)
    const normalized = normalizePointer(
      event.clientX,
      event.clientY,
      window.innerWidth,
      window.innerHeight,
    )
    const delta = Math.max(now - lastPointerAt, 8)
    const distance = Math.hypot(event.clientX - previousClientX, event.clientY - previousClientY)

    frame.kind = kind
    frame.active = true
    targetX = normalized.x * strength
    targetY = normalized.y * strength
    targetClientX = event.clientX
    targetClientY = event.clientY
    targetVelocity = Math.min(1, (distance / delta) * 0.16)
    lastInputAt = now
    lastPointerAt = now
    previousClientX = event.clientX
    previousClientY = event.clientY

    const eventTarget = event.target instanceof Element ? event.target : null
    const nextDepthTarget =
      kind === 'touch' ? null : (eventTarget?.closest<HTMLElement>('[data-pointer-depth]') ?? null)
    setDepthTarget(nextDepthTarget, event)
    interactive = Boolean(eventTarget?.closest('a, button, [data-pointer-interactive]'))
    requestFrame()
  }

  const returnToRest = () => {
    targetX = 0
    targetY = 0
    targetVelocity = 0
    targetLocalX = 0
    targetLocalY = 0
    targetPercentX = 50
    targetPercentY = 50
    frame.active = false
    interactive = false
    requestFrame()
  }

  const handlePointerLeave = () => {
    if (root.value) root.value.dataset.pointerPresent = 'false'
    clearDepthTarget()
    returnToRest()
  }

  const handleVisibility = () => {
    if (document.hidden) {
      cancelAnimationFrame(rafId)
      rafId = 0
      returnToRest()
      return
    }
    requestFrame()
  }

  function renderFrame(time: number) {
    rafId = 0
    const delta = Math.min(Math.max(time - lastFrameAt, 8), 48)
    lastFrameAt = time

    if (shouldReturnPointerToRest(time, lastInputAt, frame.kind)) {
      targetX = 0
      targetY = 0
      targetVelocity = 0
      targetLocalX = 0
      targetLocalY = 0
      targetPercentX = 50
      targetPercentY = 50
      frame.active = false
    }

    frame.x = dampPointerValue(frame.x, targetX, 8.2, delta)
    frame.y = dampPointerValue(frame.y, targetY, 8.2, delta)
    frame.clientX = dampPointerValue(frame.clientX, targetClientX, 20, delta)
    frame.clientY = dampPointerValue(frame.clientY, targetClientY, 20, delta)
    frame.velocity = dampPointerValue(frame.velocity, targetVelocity, 7, delta)
    targetVelocity *= 0.82
    localX = dampPointerValue(localX, targetLocalX, 11, delta)
    localY = dampPointerValue(localY, targetLocalY, 11, delta)
    localPercentX = dampPointerValue(localPercentX, targetPercentX, 11, delta)
    localPercentY = dampPointerValue(localPercentY, targetPercentY, 11, delta)

    if (depthTarget) {
      depthTarget.style.setProperty('--local-x', localX.toFixed(4))
      depthTarget.style.setProperty('--local-y', localY.toFixed(4))
      depthTarget.style.setProperty('--local-percent-x', `${localPercentX.toFixed(2)}%`)
      depthTarget.style.setProperty('--local-percent-y', `${localPercentY.toFixed(2)}%`)
      depthTarget.style.setProperty('--tilt-x', `${(-localY * 4.2).toFixed(2)}deg`)
      depthTarget.style.setProperty('--tilt-y', `${(localX * 5.8).toFixed(2)}deg`)
      depthTarget.style.setProperty('--magnet-x', `${(localX * 8).toFixed(2)}px`)
      depthTarget.style.setProperty('--magnet-y', `${(localY * -6).toFixed(2)}px`)
      depthTarget.style.setProperty('--layer-near-x', `${(localX * 7).toFixed(2)}px`)
      depthTarget.style.setProperty('--layer-near-y', `${(-localY * 5).toFixed(2)}px`)
      depthTarget.style.setProperty('--layer-far-x', `${(-localX * 3).toFixed(2)}px`)
      depthTarget.style.setProperty('--layer-far-y', `${(localY * 2).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-tilt-x', `${(-localY * 1.8).toFixed(2)}deg`)
      depthTarget.style.setProperty('--project-tilt-y', `${(localX * 2.4).toFixed(2)}deg`)
      depthTarget.style.setProperty('--project-image-x', `${(-localX * 5).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-image-y', `${(localY * 3.5).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-copy-x', `${(localX * 1.8).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-copy-y', `${(-localY * 1.2).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-foreground-x', `${(localX * 10).toFixed(2)}px`)
      depthTarget.style.setProperty('--project-foreground-y', `${(-localY * 7).toFixed(2)}px`)
    }

    writeRootVariables()
    subscribers.forEach((subscriber) => subscriber(frame))

    const moving =
      Math.abs(frame.x - targetX) > 0.001 ||
      Math.abs(frame.y - targetY) > 0.001 ||
      Math.abs(frame.velocity - targetVelocity) > 0.002 ||
      Math.abs(localX - targetLocalX) > 0.002 ||
      Math.abs(localY - targetLocalY) > 0.002 ||
      time - lastInputAt < 1_500

    if (moving && !disabled.value && !document.hidden) {
      rafId = requestAnimationFrame(renderFrame)
    }
  }

  const attach = () => {
    if (listenersAttached || disabled.value) return
    listenersAttached = true
    running = true
    const element = root.value
    hasFinePointer = matchMedia('(hover: hover) and (pointer: fine)').matches
    if (element) {
      element.dataset.pointerEnabled = hasFinePointer ? 'true' : 'touch'
      element.dataset.pointerPresent = 'false'
    }
    window.addEventListener('pointermove', handlePointer, { passive: true })
    window.addEventListener('pointerleave', handlePointerLeave)
    window.addEventListener('blur', handlePointerLeave)
    document.addEventListener('visibilitychange', handleVisibility)
  }

  const detach = () => {
    if (!listenersAttached) return
    listenersAttached = false
    running = false
    cancelAnimationFrame(rafId)
    rafId = 0
    clearDepthTarget()
    window.removeEventListener('pointermove', handlePointer)
    window.removeEventListener('pointerleave', handlePointerLeave)
    window.removeEventListener('blur', handlePointerLeave)
    document.removeEventListener('visibilitychange', handleVisibility)
    const element = root.value
    if (element) {
      element.dataset.pointerEnabled = 'false'
      element.dataset.pointerPresent = 'false'
      element.dataset.pointerInteractive = 'false'
      frame.x = 0
      frame.y = 0
      frame.velocity = 0
      writeRootVariables()
    }
  }

  onMounted(() => {
    frame.clientX = window.innerWidth / 2
    frame.clientY = window.innerHeight / 2
    targetClientX = frame.clientX
    targetClientY = frame.clientY
    if (disabled.value) {
      if (root.value) root.value.dataset.pointerEnabled = 'false'
    } else {
      attach()
    }
    writeRootVariables()
  })

  watch(disabled, (value) => {
    if (value) detach()
    else if (!running) attach()
  })

  onBeforeUnmount(detach)

  return {
    getSnapshot: () => ({ ...frame }),
    subscribe(listener) {
      subscribers.add(listener)
      listener(frame)
      return () => subscribers.delete(listener)
    },
  }
}
